if (!customElements.get('product-model-view')) {
  customElements.define('product-model-view', class extends HTMLElement {
    connectedCallback() {
      this.currentView = 'product';
      this.initialisedSlideshows = new WeakSet();
      this.mobileGalleriesSeen = new WeakSet();
      this.touchedGalleries = new WeakSet();

      this.handleGalleryTouch = (event) => {
        if (event.pointerType !== 'touch') return;
        if (!(event.target instanceof Element)) return;

        const slideshow = event.target.closest('.card-gallery slideshow-component');
        if (slideshow) this.touchedGalleries.add(slideshow);
      };

      this.addEventListener('pointerdown', this.handleGalleryTouch, {
        passive: true,
        capture: true
      });

      this.handlePageLoad = () => {
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        this.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
          if (!this.touchedGalleries.has(slideshow)) {
            this.initialisedSlideshows.delete(slideshow);
          }
        });

        this.scheduleNewGalleries();
      };

      window.addEventListener('load', this.handlePageLoad, { once: true });

      this.handleClick = (event) => {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest('[data-image-view-choice]');
        if (!button || !this.contains(button)) return;

        this.currentView = button.dataset.imageViewChoice === 'model'
          ? 'model'
          : 'product';

        this.syncButtons();
        this.applyView(true);
      };

      this.addEventListener('click', this.handleClick);

      this.observer = new MutationObserver((records) => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;

        if (mobile) {
          for (const record of records) {
            if (
              record.type !== 'attributes' ||
              record.attributeName !== 'in-viewport'
            ) continue;

            const slideshow = record.target;

            if (
              !(slideshow instanceof HTMLElement) ||
              !slideshow.matches('.card-gallery slideshow-component') ||
              !slideshow.hasAttribute('in-viewport') ||
              this.mobileGalleriesSeen.has(slideshow)
            ) continue;

            this.mobileGalleriesSeen.add(slideshow);

            if (!this.touchedGalleries.has(slideshow)) {
              this.initialisedSlideshows.delete(slideshow);
            }
          }
        }

        this.syncButtons();
        this.scheduleNewGalleries();
      });

      this.observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-product-view-ready', 'in-viewport']
      });

      this.syncButtons();

      customElements.whenDefined('slideshow-component').then(() => {
        if (this.isConnected) this.scheduleNewGalleries();
      });
    }

    syncButtons() {
      this.setAttribute('data-image-view', this.currentView);

      this.querySelectorAll('[data-image-view-choice]').forEach((button) => {
        const pressed = button.dataset.imageViewChoice === this.currentView;
        button.setAttribute('aria-pressed', String(pressed));
      });
    }

    scheduleNewGalleries() {
      cancelAnimationFrame(this.frame);

      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          if (this.isConnected) this.applyView(false);
        });
      });
    }

    applyView(force) {
      this.querySelectorAll('.card-gallery slideshow-component').forEach((slideshow) => {
        if (typeof slideshow.select !== 'function') return;
        if (!slideshow.hasAttribute('data-product-view-ready')) return;
        if (!force && this.initialisedSlideshows.has(slideshow)) return;
        if (!force && this.touchedGalleries.has(slideshow)) return;

        if (
          !force &&
          window.matchMedia('(max-width: 749px)').matches &&
          !slideshow.hasAttribute('in-viewport')
        ) return;

        const photoSlides = Array.from(
          slideshow.querySelectorAll(
            'slideshow-slide.product-media-container--image'
          )
        ).filter((slide) => slide.closest('slideshow-component') === slideshow);

        if (!photoSlides.length) return;

        const target = this.currentView === 'model'
          ? (photoSlides[1] || photoSlides[0])
          : photoSlides[0];

        const slideId = target.getAttribute('slide-id');
        if (!slideId) return;

        slideshow.select({ id: slideId }, undefined, { animate: false });
        this.initialisedSlideshows.add(slideshow);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('pointerdown', this.handleGalleryTouch, true);
      window.removeEventListener('load', this.handlePageLoad);
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
